import { StorePicker, StoreItem } from '@/components/StorePicker/StorePicker';
import { useAuth } from '@/auth/Auth';
import { AuthService } from '@/services/auth.service';
import LogoGlobal from '@/assets/icons/LogoGlobal';
import Mundpay from '@/assets/icons/Mundpay';
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  clearPostLoginRedirect,
  getPostLoginRedirect,
  isInvitationRedirect,
} from '@/utils/postLoginRedirect';
import { useTranslation } from 'react-i18next';
import Loader from '@/components/Loader/Loader';
import { toast } from '@/hooks/use-toast';

interface StorePickerPageProps {
  stores?: StoreItem[];
}

export default function StorePickerPage({ stores: propStores }: StorePickerPageProps) {
  const { saveToken, fetchUserData } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation('common');
  const [isSelecting, setIsSelecting] = useState(false);

  const locationStores: StoreItem[] =
    propStores ?? (location.state as { stores?: StoreItem[] } | null)?.stores ?? [];

  const pendingRedirect =
    (location.state as { from?: string } | null)?.from
    || getPostLoginRedirect();

  const handleSelectStore = async (store: StoreItem) => {
    setIsSelecting(true);
    try {
      const response = await AuthService.selectStore(store.id);
      const token = response?.data?.token;
      if (token) {
        saveToken(token);
        await fetchUserData();
        const redirectTo =
          pendingRedirect && isInvitationRedirect(pendingRedirect)
            ? pendingRedirect
            : '/dashboard';
        if (redirectTo !== '/dashboard') {
          clearPostLoginRedirect();
        }
        navigate(redirectTo, { replace: true });
      }
    } catch (error) {
      toast({
        description: t('store_select_error', { defaultValue: 'Erro ao selecionar loja' }),
        variant: 'error',
      });
    } finally {
      setIsSelecting(false);
    }
  };

  if (isSelecting) {
    return (
      <div className="flex items-center justify-center w-full h-screen bg-background">
        <Loader />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center px-4 py-8">
      <div className="w-full max-w-md space-y-8">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 flex items-center justify-center">
            <LogoGlobal />
          </div>
          <div className="space-y-2 text-center">
            <h1 className="text-2xl font-bold text-colorText">
              {t('select_store', { defaultValue: 'Selecione uma loja' })}
            </h1>
            <p className="text-sm text-muted">
              {t('select_store_description', {
                defaultValue: 'Escolha qual loja deseja acessar',
              })}
            </p>
          </div>
        </div>

        <StorePicker
          stores={locationStores}
          onSelect={handleSelectStore}
        />
      </div>
    </div>
  );
}
